// Visitors: who's here now, and how he's getting on with each person.

import { HOUR } from './time.ts';
import type { LogEvent, Millis, Reaction, Visitor } from './types.ts';

/**
 * A "new experience" for counting: a socialising entry, or a visit. The ticks
 * a visit makes (social entries with its visitId) are part of that visit, so
 * they don't count again.
 */
export const isExperience = (e: LogEvent): boolean => e.type === 'visit' || (e.type === 'social' && !e.data.visitId);

/** The visit in progress, if any (one left open for over 12 hours was never ended). */
export function openVisit(events: LogEvent[], now: Millis): LogEvent<'visit'> | undefined {
  return events.find((e): e is LogEvent<'visit'> => e.type === 'visit' && e.end === undefined && e.at <= now && now - e.at < 12 * HOUR);
}

export interface PersonStats {
  visits: number;
  last?: Millis;
  /** Reactions, oldest first. */
  reactions: Reaction[];
}

export function personStats(events: LogEvent[]): Map<string, PersonStats> {
  const m = new Map<string, PersonStats>();
  const visits = events.filter((e): e is LogEvent<'visit'> => e.type === 'visit').sort((a, b) => a.at - b.at);
  for (const v of visits)
    for (const id of v.data.people) {
      const s = m.get(id) ?? { visits: 0, reactions: [] };
      s.visits++;
      s.last = v.at;
      if (v.data.reaction) s.reactions.push(v.data.reaction);
      m.set(id, s);
    }
  return m;
}

/** People for the "Who's here?" grid: most visits first, then recently added, then by name. */
export function sortForGrid(people: Visitor[], stats: Map<string, PersonStats>): Visitor[] {
  return people
    .filter((p) => !p.archived)
    .sort((a, b) => (stats.get(b.id)?.last ?? 0) - (stats.get(a.id)?.last ?? 0) || (stats.get(b.id)?.visits ?? 0) - (stats.get(a.id)?.visits ?? 0) || a.name.localeCompare(b.name));
}

/** The socialisation items a visit from these people counts towards. */
export function visitTraits(people: Visitor[]): string[] {
  return [...new Set(people.flatMap((p) => p.traits ?? []))];
}

/** Same name already in the list (contacts imported twice)? */
export function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase().replace(/\s+/g, ' ') === b.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Two initials for an avatar: "Sam Jones" → "SJ". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** A steady colour per name, for avatars without a photo. */
export function nameHue(name: string): number {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}
